import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { CalendarDays, Clock, Building2, X, Pencil, Mail, UserPlus } from "lucide-react";
import { EditBookingDialog } from "@/components/EditBookingDialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";

export default function MemberDashboard() {
  const { user, api, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [memberProfile, setMemberProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [inbox, setInbox] = useState([]);
  const [guestName, setGuestName] = useState("");
  const [guestEmail, setGuestEmail] = useState("");
  const [guestPurpose, setGuestPurpose] = useState("");
  const [regLoading, setRegLoading] = useState(false);
  const [showGuestForm, setShowGuestForm] = useState(false);

  useEffect(() => {
    if (!isAuthenticated) { navigate("/login"); return; }
    loadData();
  }, [isAuthenticated]);

  const loadData = async () => {
    try {
      const [res, inb] = await Promise.all([
        api("get", "/bookings"),
        api("get", "/messages/inbox").catch(() => ({ data: [] })),
      ]);
      setBookings(res.data);
      setInbox(inb.data || []);
      if (user?.role === "member") {
        try {
          const profileRes = await api("get", "/member/profile");
          setMemberProfile(profileRes.data);
        } catch {}
      }
    } catch (err) {
      toast.error("Failed to load bookings");
    } finally { setLoading(false); }
  };

  const cancelBooking = async (id) => {
    try {
      const res = await api("put", `/bookings/${id}/cancel`);
      if (res.data.status === "pending_cancellation") {
        toast.info("Cancellation requested — awaiting admin approval (within 12-hour window)");
      } else {
        toast.success("Booking cancelled");
      }
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to cancel");
    }
  };

  const statusBadge = (status) => {
    const colors = {
      confirmed: "bg-emerald-100 text-emerald-700",
      pending_payment: "bg-amber-100 text-amber-700",
      pending_cancellation: "bg-orange-100 text-orange-700",
      cancelled: "bg-red-100 text-red-700",
      completed: "bg-blue-100 text-blue-700",
      no_show: "bg-gray-100 text-gray-700",
      draft: "bg-gray-100 text-gray-700",
    };
    const labels = { pending_cancellation: "cancellation pending" };
    return <Badge className={`${colors[status] || "bg-gray-100 text-gray-700"} text-xs`}>{labels[status] || status?.replace(/_/g, " ")}</Badge>;
  };

  const now = new Date().toISOString();
  const upcoming = bookings.filter(b => b.end_datetime > now && !["cancelled"].includes(b.status));
  const past = bookings.filter(b => b.end_datetime <= now || b.status === "cancelled");

  const [editBooking, setEditBooking] = useState(null);

  const canCancel = (b) => {
    if (["cancelled", "completed", "pending_cancellation"].includes(b.status)) return false;
    // Can only cancel your own bookings
    if (b.user_id !== user?.id) return false;
    return true;
  };
  const canEdit = canCancel;

  const registerGuest = async () => {
    if (!guestName.trim()) { toast.error("Guest name is required"); return; }
    setRegLoading(true);
    try {
      await api("post", "/guests/pre-register", {
        name: guestName.trim(),
        email: guestEmail.trim() || null,
        purpose: guestPurpose.trim() || "Visit",
      });
      toast.success(`${guestName.trim()} has been pre-registered${guestEmail.trim() ? " and will receive a check-in QR code" : ""}`);
      setGuestName(""); setGuestEmail(""); setGuestPurpose(""); setShowGuestForm(false);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setRegLoading(false); }
  };

  const BookingRow = ({ b }) => {
    const isMyBooking = b.user_id === user?.id;
    return (
    <TableRow>
      <TableCell>
        <div className="font-medium">{b.space_name}</div>
        {b.meeting_name && <div className="text-xs text-muted-foreground">{b.meeting_name}</div>}
        {b.booked_for_name && <div className="text-xs text-rig-teal">For: {b.booked_for_name}</div>}
        {!isMyBooking && b.user_name && <div className="text-xs text-muted-foreground">By: {b.user_name}</div>}
      </TableCell>
      <TableCell className="font-mono text-xs">{b.start_datetime?.slice(0, 10)}</TableCell>
      <TableCell className="font-mono text-xs">{b.start_datetime?.slice(11, 16)} - {b.end_datetime?.slice(11, 16)}</TableCell>
      <TableCell>{b.layout_name}</TableCell>
      <TableCell>{statusBadge(b.status)}</TableCell>
      <TableCell className="font-medium">XCG {b.price?.toFixed(2)}</TableCell>
      <TableCell>
        <div className="flex gap-1">
          {canEdit(b) && (
            <Button variant="ghost" size="sm" className="text-muted-foreground hover:text-foreground" onClick={() => setEditBooking(b)} data-testid={`edit-booking-${b.id}`}>
              <Pencil className="h-3.5 w-3.5" />
            </Button>
          )}
          {canCancel(b) && (
            <Button variant="ghost" size="sm" className="text-destructive hover:text-destructive/80" onClick={() => cancelBooking(b.id)} data-testid={`cancel-booking-${b.id}`}>
              <X className="h-4 w-4" />
            </Button>
          )}
        </div>
      </TableCell>
    </TableRow>
    );
  };

  if (loading) return <div className="max-w-6xl mx-auto px-6 py-12"><div className="h-64 bg-muted animate-pulse rounded-xl" /></div>;

  return (
    <div className="max-w-6xl mx-auto px-6 py-12 animate-fade-in" data-testid="member-dashboard">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-heading font-bold text-3xl sm:text-4xl">My Bookings</h1>
          <p className="text-muted-foreground mt-1">Your reservations and team bookings</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" className="rounded-none" onClick={() => setShowGuestForm(v => !v)} data-testid="register-guest-btn">
            <UserPlus className="h-4 w-4 mr-2" />Register Guest
          </Button>
          <Button onClick={() => navigate("/spaces")} className="bg-foreground text-background hover:bg-foreground/90 rounded-none" data-testid="new-booking-btn">
            <CalendarDays className="h-4 w-4 mr-2" />New Booking
          </Button>
        </div>
      </div>

      {/* Guest pre-registration form */}
      {showGuestForm && (
        <Card className="p-5 border mb-6 animate-fade-in" data-testid="guest-preregister-form">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-heading font-semibold text-sm flex items-center gap-2"><UserPlus className="h-4 w-4" /> Pre-register a Guest</h3>
            <Button size="sm" variant="ghost" onClick={() => setShowGuestForm(false)} data-testid="close-guest-form"><X className="h-4 w-4" /></Button>
          </div>
          <p className="text-xs text-muted-foreground mb-3">Guests with an email will receive a QR code for quick check-in at the lobby.</p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div><Label className="text-xs">Name *</Label><Input value={guestName} onChange={e => setGuestName(e.target.value)} placeholder="Guest name" data-testid="guest-reg-name" /></div>
            <div><Label className="text-xs">Email</Label><Input value={guestEmail} onChange={e => setGuestEmail(e.target.value)} placeholder="Email (optional)" data-testid="guest-reg-email" /></div>
            <div><Label className="text-xs">Purpose</Label><Input value={guestPurpose} onChange={e => setGuestPurpose(e.target.value)} placeholder="e.g. Meeting, Delivery" data-testid="guest-reg-purpose" /></div>
          </div>
          <Button className="mt-3 bg-foreground text-background rounded-none" onClick={registerGuest} disabled={regLoading} data-testid="submit-guest-reg">
            {regLoading ? "Registering..." : "Pre-register Guest"}
          </Button>
        </Card>
      )}

      {/* Member stats */}
      {memberProfile && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8" data-testid="member-stats">
          <Card className="p-5 border border-border">
            <p className="text-sm text-muted-foreground">Plan</p>
            <p className="font-heading font-semibold text-lg capitalize">{memberProfile.plan_type}</p>
          </Card>
          <Card className="p-5 border border-border">
            <p className="text-sm text-muted-foreground">Hours Used / Quota</p>
            <p className="font-heading font-semibold text-lg">{memberProfile.used_hours?.toFixed(1)}h / {memberProfile.monthly_quota_hours}h</p>
          </Card>
          <Card className="p-5 border border-border">
            <p className="text-sm text-muted-foreground">Status</p>
            <Badge className={memberProfile.status === "active" ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"}>{memberProfile.status}</Badge>
          </Card>
        </div>
      )}

      {/* Admin Messages Inbox */}
      {inbox.filter(m => !m.read).length > 0 && (
        <div className="mb-8 space-y-3" data-testid="member-inbox">
          <h2 className="font-heading font-semibold text-lg flex items-center gap-2">
            <Mail className="h-4 w-4 text-orange-500" />Messages from Admin
          </h2>
          {inbox.filter(m => !m.read).map(m => (
            <Card key={m.id} className="p-4 border border-orange-200 bg-orange-50/30" data-testid={`inbox-msg-${m.id}`}>
              <div className="flex items-start justify-between mb-1">
                <span className="font-medium text-sm">{m.subject}</span>
                <span className="text-xs text-muted-foreground">{new Date(m.created_at).toLocaleDateString()}</span>
              </div>
              <p className="text-sm text-muted-foreground whitespace-pre-line">{m.content}</p>
              <Button size="sm" variant="ghost" className="text-xs mt-1"
                onClick={async () => { try { await api("put", `/messages/inbox/${m.id}/read`); loadData(); } catch {} }}
                data-testid={`mark-read-${m.id}`}
              >Dismiss</Button>
            </Card>
          ))}
        </div>
      )}

      <Tabs defaultValue="upcoming">
        <TabsList>
          <TabsTrigger value="upcoming" data-testid="tab-upcoming">Upcoming ({upcoming.length})</TabsTrigger>
          <TabsTrigger value="past" data-testid="tab-past">Past ({past.length})</TabsTrigger>
        </TabsList>
        <TabsContent value="upcoming">
          {upcoming.length === 0 ? (
            <Card className="p-12 text-center border-dashed border-2">
              <CalendarDays className="h-12 w-12 mx-auto text-muted-foreground/40 mb-4" />
              <p className="text-muted-foreground">No upcoming bookings</p>
              <Button variant="link" onClick={() => navigate("/spaces")} className="text-primary mt-2">Browse Spaces</Button>
            </Card>
          ) : (
            <div className="rounded-lg border overflow-hidden" data-testid="upcoming-bookings-table">
              <Table>
                <TableHeader><TableRow>
                  <TableHead>Space</TableHead><TableHead>Date</TableHead><TableHead>Time</TableHead>
                  <TableHead>Layout</TableHead><TableHead>Status</TableHead><TableHead>Price</TableHead><TableHead></TableHead>
                </TableRow></TableHeader>
                <TableBody>{upcoming.map(b => <BookingRow key={b.id} b={b} />)}</TableBody>
              </Table>
            </div>
          )}
        </TabsContent>
        <TabsContent value="past">
          {past.length === 0 ? (
            <Card className="p-12 text-center border-dashed border-2">
              <p className="text-muted-foreground">No past bookings</p>
            </Card>
          ) : (
            <div className="rounded-lg border overflow-hidden" data-testid="past-bookings-table">
              <Table>
                <TableHeader><TableRow>
                  <TableHead>Space</TableHead><TableHead>Date</TableHead><TableHead>Time</TableHead>
                  <TableHead>Layout</TableHead><TableHead>Status</TableHead><TableHead>Price</TableHead><TableHead></TableHead>
                </TableRow></TableHeader>
                <TableBody>{past.map(b => <BookingRow key={b.id} b={b} />)}</TableBody>
              </Table>
            </div>
          )}
        </TabsContent>
      </Tabs>
      <EditBookingDialog
        booking={editBooking}
        open={!!editBooking}
        onClose={() => setEditBooking(null)}
        api={api}
        onSaved={loadData}
      />
    </div>
  );
}
